import { useTranslation } from 'react-i18next';
import Cleave from 'cleave.js/react';
import { Row, Col, Input, Button, Label } from 'reactstrap';
import Select from 'react-select';
import { FormikProps, useFormik } from 'formik';
import { ProductSearchFilters, SelectOptions } from 'types/productTypes';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getSearchProductsFromES,
  getSearchProductsCategory,
  getSearchSuppliers,
  ProductESSearchParams,
} from 'services/productService';
import {
  convertToProductCategoryIdOptions,
  convertToSupplierOptions,
} from 'utils/products/converter';
import { InputActionMeta, SelectedData } from 'types/common/FormTypes';
import {
  transformOptionsToListsArray,
  transformOptionsToNumberArray,
} from 'utils/common/handleSelect';
import {
  CheckboxOption,
  CheckboxTagOption,
} from 'Components/pos2u/CustomReactSelectComponents/CustomReactSelectOption';
import { ProductSearchValidationSchema } from 'pages/Products/productSearchValidation';
import { useSelector } from 'react-redux';
import { RootState } from 'slices';
import { useDispatch } from 'react-redux';
import {
  setPageFilters,
  setSelectMultiTagsFilter,
} from 'slices/product/form/reducer';
import { MultiSelectSingleLineStyles } from 'styles/common/multiSelectSingleLineStyle';
import { CustomBranchMenu } from 'Components/pos2u/CustomReactSelectComponents/CustomReactSelectBranchMenu';
import { getTags } from 'services/tagService';
import { mapTagsToOptionsNumber } from 'utils/mappers/tagMapper';
import { TagOption } from 'types/tag/TagTypes';
import { multiSelectSingleLineTagStyle } from 'styles/common/multiSelectSingleLineTagStyle';
import { CustomMultiValueTagLabel } from 'Components/pos2u/CustomReactSelectComponents/CustomMultiValueLabel';
import { cleanCommas } from 'utils/common/String';
import {
  setHasFetchedCategories,
  setHasFetchedSuppliers,
  setProductsCategoryOptions,
  setRawCategories,
  setRawSuppliers,
  setSupplierOptions,
} from 'slices/product/select/reducer';
import { useLocation } from 'react-router-dom';
import AutoComplete from 'Components/pos2u/Inputs/AutoComplete';

interface ProductSearchFormProps {
  isDarkMode: boolean;
  isAdvanceSearch: boolean;
  branchesIds: string[];
  setIsAdvanceSearch: (value: boolean) => void;
  onSubmit: (
    value: ProductSearchFilters,
    latestItemIdNumbers?: string[] | null
  ) => void;
  onSearchItemIdsChange?: (itemIdNumbers: string[] | null) => void;
  skipInitialSearchFetch?: boolean;
}
type MultiValue<T> = readonly T[];
type ActionMeta = { action: string };
type ProductESAutocompleteItem = {
  name: string;
  itemIdNumber: string;
  itemCode: string;
  categoryNumber: number | null;
  supplierId: number | null;
};

const MIN_ES_SEARCH_LENGTH = 3;
const ES_SEARCH_DEBOUNCE_MS = 1000;

const parseESAutocompleteItem = (item: string): ProductESAutocompleteItem => {
  const [
    name = '',
    itemIdNumber = '',
    itemCode = '',
    category = '',
    supplier = '',
  ] = item.split('|');
  const parsedCategoryNumber = Number(category);
  const parsedSupplierId = Number(supplier);

  return {
    name,
    itemIdNumber,
    itemCode,
    categoryNumber:
      category && Number.isFinite(parsedCategoryNumber)
        ? parsedCategoryNumber
        : null,
    supplierId:
      supplier && Number.isFinite(parsedSupplierId) ? parsedSupplierId : null,
  };
};

const ProductSearchForm = ({
  isDarkMode,
  isAdvanceSearch,
  setIsAdvanceSearch,
  onSubmit,
  onSearchItemIdsChange,
  skipInitialSearchFetch = false,
}: ProductSearchFormProps) => {
  const { t, i18n } = useTranslation();
  const infoState = useSelector((state: RootState) => state.Info);
  const { merchant, isMerchantLoaded, branches } = infoState;
  const productState = useSelector((state: RootState) => state.Product);
  const { PageFilters, selectMultiTagsFilter } = productState.form;
  const { productsCategoryOptions } = productState.select.searchCategory;
  const { supplierOptions } = productState.select.searchSuppliers;
  const { rawCategories, rawSuppliers } = productState.select;
  const { hasFetchedSuppliers, hasFetchedCategories } = productState.select;

  const [tagOptions, setTagOptions] = useState<TagOption[]>([]);
  const [inputTagValue, setInputTagValue] = useState('');
  const location = useLocation();
  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const fullSearch = query.get('full');

  const dispatch = useDispatch();

  const formik = useFormik<ProductSearchFilters>({
    initialValues: PageFilters,
    validationSchema: ProductSearchValidationSchema(t),
    enableReinitialize: true,
    onSubmit: (values) => {
      const converted = {
        ...values,
        quantityMax: cleanCommas(values.quantityMax),
        quantityMin: cleanCommas(values.quantityMin),
      };

      onSubmit(converted, latestItemIdNumbersRef.current);
      dispatch(setPageFilters(converted));
    },
  });

  const { touched, validateForm } = formik;
  useEffect(() => {
    if (i18n.language && (touched.quantityMin || touched.quantityMax)) {
      validateForm();
    }
  }, [i18n.language, touched.quantityMin, touched.quantityMax, validateForm]);

  const [inputValue, setInputValue] = useState('');
  const [productNameInput, setProductNameInput] = useState(
    PageFilters.productName
  );
  const [filteredCategoryOptions, setFilteredCategoryOptions] = useState<
    SelectOptions[]
  >(productsCategoryOptions);
  const [filteredSupplierOptions, setFilteredSupplierOptions] =
    useState<SelectOptions[]>(supplierOptions);
  const autocompleteRequestRef = useRef(0);
  const latestItemIdNumbersRef = useRef<string[] | null>(null);
  const shouldSkipInitialESFetchRef = useRef(skipInitialSearchFetch);

  const submitProductSearch = useCallback(
    (
      values: ProductSearchFilters,
      itemIdNumbers = latestItemIdNumbersRef.current
    ) => {
      const converted = {
        ...values,
        quantityMax: cleanCommas(values.quantityMax),
        quantityMin: cleanCommas(values.quantityMin),
      };

      onSubmit(converted, itemIdNumbers);
      dispatch(setPageFilters(converted));
    },
    [dispatch, onSubmit]
  );

  const categoriesById = useMemo(
    () =>
      new Map(
        rawCategories.map((category) => [category.id.toString(), category])
      ),
    [rawCategories]
  );
  const suppliersById = useMemo(
    () => new Map(rawSuppliers.map((supplier) => [supplier.id, supplier])),
    [rawSuppliers]
  );

  const fetchProductCategories = useCallback(async () => {
    if (!merchant?.id) return;
    try {
      const response = await getSearchProductsCategory(`[${merchant.id}]`);

      if (response) {
        const { message, error_code, data } = response;

        if (message === 'OK' && error_code === 0) {
          const categoryOptions = data.categories
            ? await convertToProductCategoryIdOptions(data.categories, t, true)
            : [{ options: [] }];
          dispatch(setProductsCategoryOptions(categoryOptions));
          dispatch(setRawCategories(data.categories || []));
        } else {
          dispatch(setProductsCategoryOptions([{ options: [] }]));
        }
      } else {
        console.error('No response data returned');

        dispatch(setProductsCategoryOptions([{ options: [] }]));
      }
    } catch (error) {
      console.error('Failed to fetch product categories:', error);
      dispatch(setProductsCategoryOptions([{ options: [] }]));
    }
  }, [dispatch, merchant, t]);

  const fetchSuppliers = useCallback(async () => {
    if (!merchant?.id) return;
    try {
      //! FIXME: This value is currently fixed for testing. Update this logic in the future.
      const response = await getSearchSuppliers(`[${merchant.id}]`);
      if (response) {
        const { message, error_code, data } = response;

        if (message === 'OK' && error_code === 0) {
          const supplierOptions = data.suppliers
            ? await convertToSupplierOptions(data.suppliers, t, true)
            : [{ options: [] }];
          dispatch(setSupplierOptions(supplierOptions));
          dispatch(setRawSuppliers(data.suppliers || []));
        } else {
          dispatch(setSupplierOptions([{ options: [] }]));
        }
      } else {
        console.error('No response data returned');

        dispatch(setSupplierOptions([{ options: [] }]));
      }
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
      dispatch(setSupplierOptions([{ options: [] }]));
    }
  }, [dispatch, merchant, t]);

  useEffect(() => {
    if (!hasFetchedSuppliers && isMerchantLoaded && merchant?.id) {
      fetchSuppliers();
      dispatch(setHasFetchedSuppliers(true));
    }
  }, [
    dispatch,
    fetchSuppliers,
    isMerchantLoaded,
    merchant,
    hasFetchedSuppliers,
  ]);

  useEffect(() => {
    if (!hasFetchedCategories && isMerchantLoaded && merchant?.id) {
      fetchProductCategories();
      dispatch(setHasFetchedCategories(true));
    }
  }, [
    dispatch,
    fetchProductCategories,
    isMerchantLoaded,
    merchant,
    hasFetchedCategories,
  ]);

  const preloadOptions = useCallback(async () => {
    if (rawCategories.length > 0) {
      const catOptions = await convertToProductCategoryIdOptions(
        rawCategories,
        t,
        true
      );
      dispatch(setProductsCategoryOptions(catOptions));
    }

    if (rawSuppliers.length > 0) {
      const supOptions = await convertToSupplierOptions(rawSuppliers, t, true);
      dispatch(setSupplierOptions(supOptions));
    }
  }, [rawCategories, rawSuppliers, t, dispatch]);

  useEffect(() => {
    preloadOptions();
  }, [preloadOptions]);

  useEffect(() => {
    setFilteredCategoryOptions(productsCategoryOptions);
  }, [productsCategoryOptions]);

  useEffect(() => {
    setFilteredSupplierOptions(supplierOptions);
  }, [supplierOptions]);

  useEffect(() => {
    setProductNameInput(formik.values.productName);
  }, [formik.values.productName]);

  const currentESFilters = useMemo<ProductESSearchParams>(() => {
    const selectedCategory = formik.values.category
      ? categoriesById.get(formik.values.category)
      : undefined;

    return {
      ...(productNameInput.trim() ? { name: productNameInput.trim() } : {}),
      ...(selectedCategory
        ? { category_number: selectedCategory.category_number }
        : {}),
      ...(formik.values.barcode.trim()
        ? { item_id_number: formik.values.barcode.trim() }
        : {}),
      ...(formik.values.code.trim()
        ? { item_code: formik.values.code.trim() }
        : {}),
      ...(formik.values.supplier
        ? { supplier_id: Number(formik.values.supplier) }
        : {}),
    };
  }, [
    categoriesById,
    formik.values.barcode,
    formik.values.category,
    formik.values.code,
    productNameInput,
    formik.values.supplier,
  ]);

  const hasSearchTextWithMinLength = useMemo(
    () =>
      [
        productNameInput.trim(),
        formik.values.barcode.trim(),
        formik.values.code.trim(),
      ].some((text) => text.length >= MIN_ES_SEARCH_LENGTH),
    [productNameInput, formik.values.barcode, formik.values.code]
  );

  const hasDropdownSelection = Boolean(
    formik.values.category || formik.values.supplier
  );

  const buildCategoryOptionsFromES = useCallback(
    async (items: ProductESAutocompleteItem[]) => {
      const allowedCategoryNumbers = new Set(
        items
          .map((item) => item.categoryNumber)
          .filter((value): value is number => value !== null)
      );

      const matchedCategories = rawCategories.filter((category) =>
        allowedCategoryNumbers.has(category.category_number)
      );
      const selectedCategory = formik.values.category
        ? categoriesById.get(formik.values.category)
        : undefined;
      const categoriesWithSelection =
        selectedCategory &&
        !matchedCategories.some(
          (category) => category.id === selectedCategory.id
        )
          ? [...matchedCategories, selectedCategory]
          : matchedCategories;

      if (categoriesWithSelection.length === 0) {
        return [{ options: [{ label: t('All Categories'), value: '' }] }];
      }

      return convertToProductCategoryIdOptions(
        categoriesWithSelection,
        t,
        true
      );
    },
    [categoriesById, formik.values.category, rawCategories, t]
  );

  const buildSupplierOptionsFromES = useCallback(
    async (items: ProductESAutocompleteItem[]) => {
      const allowedSupplierIds = new Set(
        items
          .map((item) => item.supplierId)
          .filter((value): value is number => value !== null)
      );

      const matchedSuppliers = rawSuppliers.filter((supplier) =>
        allowedSupplierIds.has(supplier.id)
      );
      const selectedSupplier = formik.values.supplier
        ? suppliersById.get(Number(formik.values.supplier))
        : undefined;
      const suppliersWithSelection =
        selectedSupplier &&
        !matchedSuppliers.some(
          (supplier) => supplier.id === selectedSupplier.id
        )
          ? [...matchedSuppliers, selectedSupplier]
          : matchedSuppliers;

      if (suppliersWithSelection.length === 0) {
        return [{ options: [{ label: t('All Suppliers'), value: '' }] }];
      }

      return convertToSupplierOptions(suppliersWithSelection, t, true);
    },
    [formik.values.supplier, rawSuppliers, suppliersById, t]
  );

  useEffect(() => {
    const hasFilters = Object.keys(currentESFilters).length > 0;

    if (!hasFilters || (!hasSearchTextWithMinLength && !hasDropdownSelection)) {
      shouldSkipInitialESFetchRef.current = false;
      latestItemIdNumbersRef.current = null;
      setFilteredCategoryOptions(productsCategoryOptions);
      setFilteredSupplierOptions(supplierOptions);
      return;
    }

    if (shouldSkipInitialESFetchRef.current) {
      shouldSkipInitialESFetchRef.current = false;
      return;
    }

    const requestId = ++autocompleteRequestRef.current;
    const timer = window.setTimeout(async () => {
      try {
        const response = await getSearchProductsFromES(currentESFilters);

        if (requestId !== autocompleteRequestRef.current) return;

        const items = (response.data?.limit_results?.list ?? []).map(
          parseESAutocompleteItem
        );

        const [nextCategoryOptions, nextSupplierOptions] = await Promise.all([
          buildCategoryOptionsFromES(items),
          buildSupplierOptionsFromES(items),
        ]);
        const latestItemIdNumbers = response.data?.total_results?.list ?? [];

        if (requestId !== autocompleteRequestRef.current) return;

        latestItemIdNumbersRef.current = latestItemIdNumbers;
        setFilteredCategoryOptions(nextCategoryOptions);
        setFilteredSupplierOptions(nextSupplierOptions);
      } catch (error) {
        console.error('Failed to fetch product autocomplete options:', error);

        if (requestId !== autocompleteRequestRef.current) return;

        latestItemIdNumbersRef.current = [];
        setFilteredCategoryOptions(productsCategoryOptions);
        setFilteredSupplierOptions(supplierOptions);
      }
    }, ES_SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
    // หมายเหตุ: ไม่ใส่ skipInitialSearchFetch ใน deps เพราะ effect ใช้ผ่าน
    // shouldSkipInitialESFetchRef (จับค่าตอน mount) ถ้าใส่จะทำให้ effect re-run
    // ตอนเปลี่ยนหน้า pagination (prop นี้ผูกกับ cacheKey ที่มี currentPage) → ยิง ES ซ้ำ
  }, [
    buildCategoryOptionsFromES,
    buildSupplierOptionsFromES,
    currentESFilters,
    hasDropdownSelection,
    hasSearchTextWithMinLength,
    productsCategoryOptions,
    supplierOptions,
  ]);

  const handleInputChange = (newValue: string, meta: InputActionMeta) => {
    if (meta.action === 'input-change') {
      setInputValue(newValue);
    }
  };
  const handleChangeBranch = (selectedOptions: SelectedData[]) => {
    formik
      .setFieldValue(`branch`, transformOptionsToListsArray(selectedOptions))
      .then(async () => formik.setFieldTouched(`branch`, true));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formik.values.productName !== productNameInput) {
      const nextValues = {
        ...formik.values,
        productName: productNameInput,
      };

      void formik.setValues(nextValues, false);
      submitProductSearch(nextValues);
      return;
    }
    formik.handleSubmit();
  };

  const handleSearch = useCallback(async (term: string) => {
    if (term.length < 3) return;

    try {
      const results = await getTags(term, 1, -1, 1);

      if (results) {
        const { message, error_code, data } = results;

        if (message === 'OK' && error_code === 0) {
          if (data && data.Tags) {
            const searchResultsOptions = await mapTagsToOptionsNumber(
              data.Tags
            );
            setTagOptions(searchResultsOptions);
          } else {
            setTagOptions([]);
          }
        } else {
          setTagOptions([]);
        }
      } else {
        console.error('No response data returned');
        setTagOptions([]);
      }
    } catch (error) {
      console.log(error);
      setTagOptions([]);
    }
  }, []);
  const handleInputProductTagsChange = (newValue: string) => {
    setInputTagValue(newValue);
    handleSearch(newValue);
  };

  const handleChangeProductTags = (
    selectedOptions: MultiValue<SelectedData>,
    _actionMeta: ActionMeta
  ) => {
    const selected = selectedOptions as SelectedData[];
    dispatch(setSelectMultiTagsFilter(selected));

    formik.setFieldValue('tag_ids', transformOptionsToNumberArray(selected));
    formik.setFieldTouched('tag_ids', true);
  };

  const handleChangeNumberInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    formik: FormikProps<ProductSearchFilters>
  ) => {
    const { name, value } = e.target;

    const rawValue = cleanCommas(value);

    formik.setFieldValue(name, rawValue).then(async () => {
      formik.setFieldTouched(name, true);
    });
  };

  const selectedCategoryOption = useMemo(
    () =>
      filteredCategoryOptions
        .flatMap((group: SelectOptions) => group.options)
        .find((option: { value: string }) =>
          formik.values.category
            ? option.value === formik.values.category
            : option.value === ''
        ),
    [filteredCategoryOptions, formik.values.category]
  );

  const categoryAutoCompleteOptions = useMemo(
    () =>
      filteredCategoryOptions.flatMap((group: SelectOptions) => group.options),
    [filteredCategoryOptions]
  );

  const selectedSupplierOption = useMemo(
    () =>
      filteredSupplierOptions
        .flatMap((group: SelectOptions) => group.options)
        .find((option: { value: string }) =>
          formik.values.supplier
            ? option.value === formik.values.supplier
            : option.value === ''
        ),
    [filteredSupplierOptions, formik.values.supplier]
  );

  const supplierAutoCompleteOptions = useMemo(
    () =>
      filteredSupplierOptions.flatMap((group: SelectOptions) => group.options),
    [filteredSupplierOptions]
  );

  const marginStyle = useMemo(() => {
    return `me-2 mb-2`;
  }, []);
  const gapStyle = useMemo(() => {
    return `g-0`;
  }, []);
  const lgCol = useMemo(() => {
    return `4`;
  }, []);
  const mdCol = useMemo(() => {
    return `12`;
  }, []);
  const mdCol2 = useMemo(() => {
    return `6`;
  }, []);

  return (
    <form onSubmit={handleSubmit} data-testid="productSearchForm">
      <Row className={`${gapStyle}`}>
        <Col md={9} lg={9}>
          <Row className={`${gapStyle}`}>
            <Col md={mdCol} lg={lgCol} className="col-xxl-4">
              <div className={`search-box ${marginStyle}`}>
                {/* <Input
                  type="text"
                  id="productName"
                  name="productName"
                  placeholder={t('Item Name')}
                  value={formik.values.productName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  style={{ height: 38 }}
                  data-testid="search-form-productName"
                /> */}
                <AutoComplete
                  id="productName"
                  name="productName"
                  searchType="name"
                  onOptionSelect={(option, totalResultIds) => {
                    const ids = option ? totalResultIds : null;
                    const nextProductName = option?.value || '';
                    const nextValues = {
                      ...formik.values,
                      productName: nextProductName,
                    };

                    latestItemIdNumbersRef.current = ids;
                    onSearchItemIdsChange?.(ids);
                    setProductNameInput(nextProductName);
                    void formik.setValues(nextValues, false);
                    submitProductSearch(nextValues, ids);
                  }}
                  onInputValueChange={(nextValue) => {
                    setProductNameInput(nextValue);
                  }}
                  value={formik.values.productName}
                  onBlur={() => {
                    formik.setFieldTouched('productName', true);
                    if (formik.values.productName !== productNameInput) {
                      formik.setFieldValue(
                        'productName',
                        productNameInput,
                        false
                      );
                    }
                  }}
                  isDarkMode={isDarkMode}
                  allValues={currentESFilters}
                  placeholder={t('Item Name')}
                />
                {/* <i className="bx bx-search-alt search-icon"></i> */}
              </div>
            </Col>
            <Col md={mdCol} lg={lgCol} className="col-xxl-4 ">
              <div
                className={`${marginStyle}`}
                data-testid="search-form-branch"
              >
                <Select
                  id="branch"
                  name="branch"
                  aria-label="branch"
                  value={branches.filter(
                    (option) =>
                      Array.isArray(formik.values.branch) &&
                      formik.values.branch.includes(option.value)
                  )}
                  isMulti={true}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  inputValue={inputValue}
                  onInputChange={handleInputChange}
                  onChange={handleChangeBranch}
                  onBlur={() => {
                    formik.setFieldTouched('branch', true);
                  }}
                  options={branches}
                  components={{
                    Menu: CustomBranchMenu,
                    Option: CheckboxOption,
                  }}
                  styles={MultiSelectSingleLineStyles(isDarkMode)}
                  placeholder={t('Branch')}
                />
              </div>
            </Col>

            <Col md={mdCol} lg={lgCol} className="col-xxl-4 ">
              <div
                className={`${marginStyle}`}
                data-testid="search-form-category"
              >
                <AutoComplete
                  id="category"
                  name="category"
                  value={formik.values.category}
                  selectedOption={selectedCategoryOption}
                  staticOptions={categoryAutoCompleteOptions}
                  onChange={(option) => {
                    formik
                      .setFieldValue('category', option?.value || '')
                      .then(async () => {
                        await formik.setFieldTouched('category', true);
                        formik.handleSubmit();
                      });
                  }}
                  onBlur={() => {
                    formik.setFieldTouched('category', true);
                  }}
                  isDarkMode={isDarkMode}
                  minSearchLength={0}
                  placeholder={t('SearchProductCategory')}
                />
              </div>
            </Col>
          </Row>
          {isAdvanceSearch && (
            <>
              <Row className={`${gapStyle}`}>
                <Col md={mdCol} lg={lgCol} className="col-xxl-4">
                  <div
                    className={`${marginStyle}`}
                    data-testid="search-form-code"
                  >
                    <AutoComplete
                      id="code"
                      name="code"
                      value={formik.values.code}
                      searchType="item_code"
                      onOptionSelect={(option, totalResultIds) => {
                        const ids = option ? totalResultIds : null;
                        const nextValues = {
                          ...formik.values,
                          code: option?.value || '',
                        };

                        latestItemIdNumbersRef.current = ids;
                        onSearchItemIdsChange?.(ids);
                        void formik.setValues(nextValues, false);
                        submitProductSearch(nextValues, ids);
                      }}
                      onInputValueChange={(nextValue) => {
                        formik.setFieldValue('code', nextValue, false);
                      }}
                      onBlur={() => {
                        formik.setFieldTouched('code', true);
                      }}
                      isDarkMode={isDarkMode}
                      allValues={currentESFilters}
                      placeholder={t('SearchProductCode')}
                    />
                  </div>
                </Col>
                <Col md={mdCol} lg={lgCol} className="col-xxl-4">
                  <div
                    className={`${marginStyle}`}
                    data-testid="search-form-barcode"
                  >
                    <AutoComplete
                      id="barcode"
                      name="barcode"
                      value={formik.values.barcode}
                      searchType="item_id_number"
                      onOptionSelect={(option, totalResultIds) => {
                        const ids = option ? totalResultIds : null;
                        const nextValues = {
                          ...formik.values,
                          barcode: option?.value || '',
                        };

                        latestItemIdNumbersRef.current = ids;
                        onSearchItemIdsChange?.(ids);
                        void formik.setValues(nextValues, false);
                        submitProductSearch(nextValues, ids);
                      }}
                      onInputValueChange={(nextValue) => {
                        formik.setFieldValue('barcode', nextValue, false);
                      }}
                      onBlur={() => {
                        formik.setFieldTouched('barcode', true);
                      }}
                      isDarkMode={isDarkMode}
                      allValues={currentESFilters}
                      placeholder={t('SearchProductBarcode')}
                    />
                  </div>
                </Col>
                <Col md={mdCol} lg={lgCol} className="col-xxl-4">
                  <div
                    className={`${marginStyle}`}
                    data-testid="search-form-category"
                  >
                    <AutoComplete
                      id="supplier"
                      name="supplier"
                      value={formik.values.supplier}
                      selectedOption={selectedSupplierOption}
                      staticOptions={supplierAutoCompleteOptions}
                      onChange={(option) => {
                        formik
                          .setFieldValue('supplier', option?.value || '')
                          .then(async () => {
                            await formik.setFieldTouched('supplier', true);
                            formik.handleSubmit();
                          });
                      }}
                      onBlur={() => {
                        formik.setFieldTouched('supplier', true);
                      }}
                      isDarkMode={isDarkMode}
                      minSearchLength={0}
                      placeholder={t('SearchProductSupplier')}
                    />
                  </div>
                </Col>
              </Row>
              <Row className={`${gapStyle}`}>
                {fullSearch === '1' && (
                  <>
                    <Col md={mdCol2} lg={lgCol} className="col-xxl-2">
                      <div
                        className={`${marginStyle}`}
                        data-testid="search-form-quantityMin"
                      >
                        <Cleave
                          className={`form-control`}
                          name={`quantityMin`}
                          value={formik.values.quantityMin || ''}
                          placeholder={t('MinQuantity')}
                          options={{
                            numeral: true,
                            numeralDecimalScale: 2,
                            numeralIntegerScale: 9,
                            numeralThousandsGroupStyle: 'thousand',
                          }}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            handleChangeNumberInput(e, formik);
                          }}
                          onBlur={formik.handleBlur}
                          style={{ height: 38 }}
                          data-testid="search-form-quantityMin"
                        />
                        {formik.isValid === false &&
                        formik.errors.quantityMin ? (
                          <div className="text-danger">
                            {formik.errors.quantityMin}
                          </div>
                        ) : null}
                      </div>
                    </Col>

                    <Col md={mdCol2} lg={lgCol} className="col-xxl-2">
                      <div
                        className={`${marginStyle}`}
                        data-testid="search-form-quantityMax"
                      >
                        <Cleave
                          className={`form-control`}
                          name={`quantityMax`}
                          value={formik.values.quantityMax || ''}
                          placeholder={t('MaxQuantity')}
                          options={{
                            numeral: true,
                            numeralDecimalScale: 2,
                            numeralIntegerScale: 9,
                            numeralThousandsGroupStyle: 'thousand',
                          }}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            handleChangeNumberInput(e, formik);
                          }}
                          onBlur={formik.handleBlur}
                          style={{ height: 38 }}
                          data-testid="search-form-quantityMax"
                        />
                        {formik.isValid === false &&
                        formik.errors.quantityMax ? (
                          <div className="text-danger">
                            {formik.errors.quantityMax}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                  </>
                )}

                <Col md={mdCol} lg={lgCol} className="col-xxl-4">
                  <div
                    className={`${marginStyle}`}
                    data-testid="search-form-category"
                  >
                    <Select
                      id="product-tags"
                      name="product-tags"
                      components={{
                        MultiValueLabel: CustomMultiValueTagLabel,
                        Option: CheckboxTagOption,
                      }}
                      value={selectMultiTagsFilter}
                      isMulti
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      onChange={handleChangeProductTags}
                      onBlur={() => {
                        formik.setFieldTouched('tag_ids', true);
                      }}
                      onInputChange={handleInputProductTagsChange}
                      inputValue={inputTagValue}
                      options={tagOptions}
                      formatOptionLabel={(option: SelectedData) => (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {option.label}
                        </div>
                      )}
                      styles={multiSelectSingleLineTagStyle(isDarkMode)}
                      placeholder={t('Tags')}
                    />
                  </div>
                </Col>
              </Row>
            </>
          )}
        </Col>
        <Col md={3} lg={3}>
          <div className="d-flex mx-lg-2 mx-md-0">
            <Button
              color="success"
              type="submit"
              outline
              data-testid="search-form-btn-submit"
            >
              {t('Search')}
            </Button>
            <div className="form-check form-check-success mx-3 text-muted">
              <Input
                type="checkbox"
                className="form-check-input"
                id="advance-search"
                checked={isAdvanceSearch}
                onChange={(e) => {
                  if (!e.target.checked) {
                    formik.setFieldValue('supplier', '').then(() => {
                      formik.setFieldTouched('supplier', true);
                    });
                    formik.setFieldValue('barcode', '').then(() => {
                      formik.setFieldTouched('barcode', true);
                    });
                    formik.setFieldValue('quantityMin', '').then(() => {
                      formik.setFieldTouched('quantityMin', true);
                    });
                    formik.setFieldValue('quantityMax', '').then(() => {
                      formik.setFieldTouched('quantityMax', true);
                    });
                    formik.setFieldValue('code', '').then(() => {
                      formik.setFieldTouched('code', true);
                    });
                    formik.setFieldValue('tag_ids', []).then(() => {
                      formik.setFieldTouched('tag_ids', true);
                    });
                    dispatch(setSelectMultiTagsFilter([]));
                  }
                  setIsAdvanceSearch(!isAdvanceSearch);
                }}
              />
              <Label className="form-check-label" for="advance-search">
                {t('Advanced search')}
              </Label>
            </div>
          </div>
        </Col>
      </Row>
    </form>
  );
};
export default ProductSearchForm;
